import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import SatModal from '@components/common/satUI/SatModal';
import SatButton from '@components/common/satUI/SatButton';
import { CustomerInfoPreviousAddress } from '@container/customer';
import { isMobile } from '@common/utils/device';

const PostCodeSearchPopup = props => {
  const { type, visible, setVisible, onCompleteAddress = undefined } = props;

  const [popupVisible, setPopupVisible] = useState(false);
  const [detailView, setDetailView] = useState(false);
  const [query, setQuery] = useState('');
  const [addressInfo, setAddressInfo] = useState({
    zoneCode: '', // 우편번호
    userSelectedType: '', // 주소 타입 - 지번: 01 / 도로명: 02
    address: '', // 주소
  });

  const detailRef = useRef();

  const fallbackAddressModal = () => {
    setAddressInfo({
      zoneCode: '06253',
      userSelectedType: $utils.getAddressTypeCode('R'),
      address: '서울 강남구 강남대로 298 (역삼동, 푸르덴셜)',
    });
    setDetailView(true);
  };

  const callDaumPostCode = () => {
    const elementWrap = document.getElementById('wrap');

    try {
      new window.daum.Postcode({
        width: '100%',
        height: '450px',
        focusInput: true,
        // 주소 한글/영문 함께 보기 기능(default:false)
        alwaysShowEngAddr: false,
        // 지도 버튼 숨기기
        hideMapBtn: true,
        // 영문보기 버튼 숨기기
        hideEngBtn: true,
        // 주소선택시 callback event
        oncomplete: data => {
          const addressType = data.userSelectedType;
          const addressText = $utils.makeAddressText(data);

          setAddressInfo({
            ...addressInfo,
            zoneCode: data.zonecode,
            userSelectedType: $utils.getAddressTypeCode(addressType),
            address: addressText,
          });
          setQuery(data.query);
        },
        onsearch: data => console.log('### onsearch => ', data),
        // 주소선택후 카카오 api가 종료될때 callback event
        onclose: state => setDetailView(true),
      }).embed(elementWrap, { q: query, autoClose: true });
    } catch (error) {
      console.log('callDaumPostCode() - error', error);

      fallbackAddressModal();
    }
  };

  const handlePrevButton = () => {
    if (isMobile.any()) {
      setDetailView(false);
      callDaumPostCode();
    } else {
      setPopupVisible(false);
    }
  };

  const handleCompleteAddress = () => {
    const detailAddress = detailRef.current.getDetailAddress();
    if (!detailAddress?.trim())
      return detailRef.current.setIsShowDetailError(true);

    onCompleteAddress &&
      onCompleteAddress({
        ...addressInfo,
        detailAddress,
      });
    setPopupVisible(false);
  };

  const handleSelectPreviousAddress = selectedAddressInfo => {
    const { zipCd, addr, addrDtl, addrType } = selectedAddressInfo;
    onCompleteAddress({
      userSelectedType: addrType,
      zoneCode: zipCd,
      address: addr,
      detailAddress: addrDtl,
    });
    setPopupVisible(false);
  };

  useEffect(() => {
    if (visible) {
      setPopupVisible(true);
    } else {
      setQuery('');
    }
  }, [visible]);

  useEffect(() => {
    if (popupVisible) {
      if ($utils.checkExternal()) {
        callDaumPostCode();
      } else {
        fallbackAddressModal();
      }
    } else {
      setDetailView(false);
      setVisible(false);
    }
  }, [popupVisible]);

  const DetailAddress = forwardRef((props, ref) => {
    const [isShowDetailError, setIsShowDetailError] = useState(false);
    const [detailAddress, setDetailAddress] = useState('');
    const handleDetailAddress = e => setDetailAddress(e.target.value);

    useEffect(() => setIsShowDetailError(false), [detailAddress]);

    useImperativeHandle(ref, () => ({
      setIsShowDetailError,
      getDetailAddress() {
        return detailAddress;
      },
    }));

    return (
      <>
        <div className="address-wrap">
          <dl className="address-detail">
            <dt>우편번호</dt>
            <dd className="c-blue bold">{addressInfo.zoneCode}</dd>
            {
              <>
                <dt>
                  {/* 01: 지번 / 02: 도로명 */}
                  {addressInfo.userSelectedType === '01'
                    ? '지번 주소'
                    : '도로명 주소'}
                </dt>
                <dd>{addressInfo.address}</dd>
              </>
            }
          </dl>

          <div className="input-wrap u-line val readonly">
            <div className="input-area">
              <input
                type="text"
                id="name"
                className="input line1"
                value={detailAddress}
                maxLength={20}
                placeholder="나머지 상세주소를 입력해 주세요."
                onChange={handleDetailAddress}
              />
            </div>
          </div>
        </div>

        {isShowDetailError && (
          <p className="ico-type text-info error">상세주소를 입력해주세요.</p>
        )}
      </>
    );
  });

  return (
    <SatModal
      visible={popupVisible}
      setVisible={() => {
        setPopupVisible(false);
      }}
      wide
    >
      <header className="modal-header">
        <h3 className="layer-tit2">주소 검색</h3>
        {detailView && (
          <SatButton
            styleClass="btn-back"
            label="이전"
            onClick={handlePrevButton}
          />
        )}
      </header>
      <div className="cont">
        <div id="wrap" style={{ display: detailView ? 'none' : 'block' }} />
        {detailView ? (
          <DetailAddress ref={detailRef} />
        ) : (
          <></>

          /* Track2: 기등록 주소/기등록 이메일 기능 삭제 */
          // <CustomerInfoPreviousAddress
          //   type={type}
          //   onSelect={handleSelectPreviousAddress}
          // />
        )}
      </div>

      {detailView && (
        <>
          <div className="layer-footer">
            <SatButton
              styleClass="btn-s blue btn-wide"
              label="확인"
              onClick={handleCompleteAddress}
            />
          </div>

          <div className="popup_foot show">
            <span className="inner_footer">
              <span className="txt_by">Powered by </span>
              <strong className="img_post tit_kakao">kakao</strong>
              <span className="txt_bar"></span>
              <span className="link_infomation" rel="noreferrer">
                우편번호 서비스 안내
              </span>

              {/* <a
                href="//postcode.map.daum.net/guide"
                className="link_infomation"
                target="_blank"
                rel="noreferrer"
              >
                우편번호 서비스 안내
              </a> */}
            </span>
          </div>
        </>
      )}
    </SatModal>
  );
};

PostCodeSearchPopup.propTypes = {
  type: $propTypes.oneOf(['home', 'workplace', '']),
  visible: $propTypes.bool,
  setVisible: $propTypes.func,
  onCompleteAddress: $propTypes.func,
};

PostCodeSearchPopup.defaultProps = {
  type: '',
};

export default PostCodeSearchPopup;
